import { useState } from 'react';
import { api, ApiError, type Account } from '../api';
import type { Dictionary, Locale } from '../i18n';

/**
 * Pasek potwierdzenia adresu. Pokazujemy go zamiast czekać, aż użytkownik zderzy się
 * z odmową przy zakładaniu firmy.
 */
export function EmailVerificationNotice({
  account,
  locale,
  t,
  onVerified,
}: {
  account: Account;
  locale: Locale;
  t: Dictionary;
  onVerified: () => void;
}) {
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [resent, setResent] = useState(false);

  async function confirm() {
    setBusy(true);
    setError(null);
    try {
      await api.verifyEmail(account.email, code.trim());
      onVerified();
    } catch (failure) {
      setError(failure instanceof ApiError ? failure.message : String(failure));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="warning">
      <p>{t.verification.notice}</p>
      {error && <p className="error">{error}</p>}
      <form
        className="form-row"
        onSubmit={(event) => {
          event.preventDefault();
          void confirm();
        }}
      >
        <label className="inline">
          {t.verification.code}
          <input
            value={code}
            onChange={(event) => setCode(event.target.value)}
            inputMode="numeric"
            maxLength={6}
            className="narrow"
            required
          />
        </label>
        <button type="submit" className="primary small-button" disabled={busy}>
          {t.verification.confirm}
        </button>
        <button
          type="button"
          className="ghost small-button"
          disabled={busy}
          onClick={() =>
            void (async () => {
              setBusy(true);
              try {
                await api.resendVerification(account.email, locale);
                setResent(true);
              } finally {
                setBusy(false);
              }
            })()
          }
        >
          {t.verification.resend}
        </button>
        {resent && <span className="muted small">{t.verification.resent}</span>}
      </form>
    </div>
  );
}
